import { clsx } from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { type KeyboardEvent, type ReactNode, type RefObject, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { type DismissReason, useDismiss, usePopoverPosition } from './overlay';

export type DropdownMenuItem =
  | {
      type?: 'item';
      key: string;
      label: ReactNode;
      icon?: LucideIcon;
      onSelect: () => void;
      danger?: boolean;
      disabled?: boolean;
      /** Right-aligned hint such as a keyboard shortcut. */
      hint?: string;
    }
  | { type: 'separator'; key: string }
  | { type: 'label'; key: string; label: ReactNode };

/** Spread onto the element that opens the menu (Button/IconButton accept all of these). */
export type DropdownTriggerProps = {
  ref: RefObject<HTMLButtonElement | null>;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  'aria-haspopup': 'menu';
  'aria-expanded': boolean;
  'aria-controls': string | undefined;
};

export type DropdownMenuProps = {
  trigger: (props: DropdownTriggerProps) => ReactNode;
  items: readonly DropdownMenuItem[];
  align?: 'start' | 'end';
  /** Menu width classes (default min-w-44). */
  className?: string;
  'aria-label'?: string;
};

function enabledItems(menu: HTMLElement | null): HTMLElement[] {
  return Array.from(menu?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? []);
}

/**
 * Click-to-open action menu. ↑/↓/Home/End move between items, Enter/Space select, Esc closes and returns
 * focus to the trigger, outside click closes. ArrowDown on the trigger opens it.
 */
export function DropdownMenu({ trigger, items, align = 'start', className, 'aria-label': ariaLabel }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const placement = usePopoverPosition(triggerRef, menuRef, open, align);

  const close = useCallback((reason: DismissReason | 'select') => {
    setOpen(false);
    if (reason !== 'outside') triggerRef.current?.focus();
  }, []);
  useDismiss([triggerRef, menuRef], open, close);

  useEffect(() => {
    if (open) enabledItems(menuRef.current)[0]?.focus({ preventScroll: true });
  }, [open]);

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const elements = enabledItems(menuRef.current);
    const index = elements.indexOf(document.activeElement as HTMLElement);
    let next: HTMLElement | undefined;
    if (event.key === 'ArrowDown') next = elements[(index + 1) % elements.length];
    else if (event.key === 'ArrowUp') next = elements[(index - 1 + elements.length) % elements.length];
    else if (event.key === 'Home') next = elements[0];
    else if (event.key === 'End') next = elements[elements.length - 1];
    else if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };

  const triggerProps: DropdownTriggerProps = {
    ref: triggerRef,
    onClick: () => setOpen((v) => !v),
    onKeyDown: (event) => {
      if (event.key === 'ArrowDown' && !open) {
        event.preventDefault();
        setOpen(true);
      }
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
  };

  return (
    <>
      {trigger(triggerProps)}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={ariaLabel}
            onKeyDown={onMenuKeyDown}
            style={{ position: 'fixed', top: placement?.top ?? -9999, left: placement?.left ?? -9999 }}
            className={clsx(
              'z-[60] animate-pop-in rounded-lg border border-slate-200 bg-surface p-1 shadow-popover',
              className ?? 'min-w-44',
            )}
          >
            {items.map((item) => {
              if (item.type === 'separator') return <div key={item.key} role="separator" className="my-1 h-px bg-slate-100" />;
              if (item.type === 'label')
                return (
                  <div key={item.key} className="truncate px-2 pt-1.5 pb-1 text-xs text-slate-500">
                    {item.label}
                  </div>
                );
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  disabled={item.disabled}
                  onClick={() => {
                    close('select');
                    item.onSelect();
                  }}
                  className={clsx(
                    'flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] outline-none disabled:opacity-40',
                    item.danger ? 'text-red-600 focus:bg-red-50' : 'text-slate-700 focus:bg-slate-100 focus:text-slate-900',
                  )}
                  onMouseEnter={(e) => e.currentTarget.focus({ preventScroll: true })}
                >
                  {Icon && <Icon size={15} aria-hidden className={item.danger ? 'text-red-500' : 'text-slate-400'} />}
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.hint && <span className="text-2xs text-slate-400">{item.hint}</span>}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
